import React, { useContext, useEffect } from 'react';
import { useMutation } from 'react-query';
import { Basket, Page } from '../..';
import { appStore } from '../../../state';
import { Copy, UserVoucherData, ROUTES } from '../../../types';
import {
  generateSignedRequestConfig,
  pricePresenter,
  textReplacer,
} from '../../../utils';
import PaymentMethod from './PaymentMethods';
import { baseURL, patchVoucherToProfile } from '../../../api';
import { useGetVfAvailability } from '../../../hooks';
import { useHistory } from 'react-router-dom';

interface PaymentProps extends Copy { }

const Payment = ({ fallbackCopy, localeCopy }: PaymentProps) => {
  const { dispatch, state } = useContext(appStore);
  const history = useHistory();
  const { form, config, user, skipPayment } = state;
  const { selectedPlan } = form;
  const { billingCycle, currency, totalPrice, productId } = selectedPlan!;
  const getVfAvailabilityQuery = useGetVfAvailability(productId, user)

  const { currencySymbols } = config;
  const { apiKey, apiSecret, countryCode, id } = user;
  const {
    isError: voucherToProfileIsError,
    isLoading: voucherToProfileIsLoading,
    mutateAsync: matchVoucherWithProfile,
  } = useMutation(patchVoucherToProfile, {
    onSuccess: (data: UserVoucherData) => {
      /**
       * Save data to state
       * Keep current user ID and save new ID from voucher as voucherID
       */
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          ...data,
          id: user.id,
          voucherID: data.id
        },
      });
      //called when the user submits a voucher
      //if the voucher doesn't require payment and also removes 100% of the price, then go directly to the success screen
      //backend should return onlyValidWithPayment: true for the latter case, but checking percentage is a sanity check incase it doesn't
      if (data.onlyValidWithPayment === false && data.percentageDiscount === 100)
      {
        history.push(ROUTES.PAYMENT_SUCCESS);
      }
    },
    onError: async (error: any) => {
      console.error('Error with patching voucher: ', await error);
      const { status } = await error;
      if (status === 409) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.VOUCHER_REDEEMED ||
              fallbackCopy.errors.VOUCHER_REDEEMED,
            timeout: 7,
            type: 'error',
          },
        });
        return;
      }
      if (status === 422) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.VOUCHER_NOT_REDEEMED ||
              fallbackCopy.errors.VOUCHER_NOT_REDEEMED,
            timeout: 7,
            type: 'error',
          },
        });
        history.replace(ROUTES.DASHBOARD);
        return;
      }

      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          dismissable: true,
          id: new Date().getTime(),
          text:
            localeCopy?.errors?.GENERIC_ERROR ||
            fallbackCopy.errors.GENERIC_ERROR,
          timeout: 7,
          type: 'error',
        },
      });
    },
  });

  useEffect(() => {
    getVfAvailabilityQuery.refetch();
    (async () => {
      /**
       * If the user has a valid voucher code, use it to match the voucher code to the
       * user profile. BE requirement
       */
      const discount = !!form.selectedPlan ? form.selectedPlan.discounts[0] : null
      if (!!form.voucherCode && !user.campaign) {
        await matchVoucherWithProfile(
          generateSignedRequestConfig({
            apiKey,
            apiSecret,
            countryCode,
            data: {
              voucherCode: form.voucherCode,
            },
            id,
            method: 'PATCH',
            url: `${baseURL}/api/billing/user/${id}/voucher`,
          })
        );
      }
      //called if the user applies a free voucher code to their account, reloads before
      //the user has completed the process, and then tries to finalise the plan
      //if the voucher doesn't require payment and also removes 100% of the price, then go directly to the success screen
      //backend should return onlyValidWithPayment: true for the latter case, but checking percentage is a sanity check incase it doesn't
      else if (!!form.voucherCode && !!user.campaign && (!!discount && discount.onlyValidWithPayment === false && discount.percentage === 100))
      {
        history.push(ROUTES.PAYMENT_SUCCESS);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    getVfAvailabilityQuery.isLoading ||
    voucherToProfileIsLoading ||
    voucherToProfileIsError ||
    skipPayment?.skip
  ) {
    return <p>...Loading...</p>;
  }

  return (
    <Page
      isReady={true}
      pageNumber={3}
      configEnablePageCounter={state.config.shouldShowPageCounter}
      title={localeCopy?.title || fallbackCopy.title}>
      <Basket
        {...selectedPlan}
        priceFrequency={
          localeCopy?.plans[billingCycle.toLowerCase()]?.priceFrequency ||
          fallbackCopy.plans[billingCycle.toLowerCase()].priceFrequency
        }
        title={
          localeCopy?.plans[billingCycle.toLowerCase()]?.title ||
          fallbackCopy.plans[billingCycle.toLowerCase()].title
        }
        currencySymbols={currencySymbols}
        accentMessage={(selectedPlan?.discounts && selectedPlan?.discounts?.length !== 0) ?
          ((selectedPlan?.discounts[0]?.percentage < 100) ?
            (textReplacer(
              localeCopy?.plans[billingCycle.toLowerCase()]?.accentSecondaryMessage ||
              fallbackCopy.plans[billingCycle.toLowerCase()].accentSecondaryMessage,
              {
                '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                  localeCopy?.month?.singular || fallbackCopy.month.singular) :
                  (localeCopy?.month?.plural || fallbackCopy.month.plural)
              })
            ) :
            (
              (textReplacer(
                localeCopy?.plans[billingCycle.toLowerCase()]?.accentTertiaryMessage ||
                fallbackCopy.plans[billingCycle.toLowerCase()].accentTertiaryMessage,
                {
                  '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                  '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                  '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                    localeCopy?.month?.singular || fallbackCopy.month.singular) :
                    (localeCopy?.month?.plural || fallbackCopy.month.plural)
                })
              )
            )
          ) :
          (
            localeCopy?.plans[billingCycle.toLowerCase()]?.accentMessage ||
            fallbackCopy.plans[billingCycle.toLowerCase()].accentMessage
          )
        }
        detailsMessage={textReplacer(
          localeCopy?.plans[billingCycle.toLowerCase()]?.detailsMessage ||
          fallbackCopy.plans[billingCycle.toLowerCase()].detailsMessage,
          {
            '{PRICE}': pricePresenter(totalPrice, currencySymbols, currency)
          }
        )
        }
        additionalInfo={
          localeCopy?.plans[billingCycle.toLowerCase()]?.additionalInfo ||
          fallbackCopy.plans[billingCycle.toLowerCase()].additionalInfo
        }
        details={(selectedPlan!.discounts?.length !== 0 && selectedPlan!.discounts[0]?.percentage < 100) ?
          localeCopy?.plans[billingCycle.toLowerCase()]?.details ||
          fallbackCopy.plans[billingCycle.toLowerCase()].details
          :
          undefined
        }
      />
      <PaymentMethod
        fallbackCopy={fallbackCopy}
        localeCopy={localeCopy}
        paymentMethods={config.paymentMethods}
        productId={selectedPlan?.productId}
        //check if vodafone API returns true
        showVFPay={getVfAvailabilityQuery.data}
        voucherData={selectedPlan?.discounts}
      />
    </Page>
  );
};

export default Payment;
